import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  fillApplicationErrorMessage,
  type FillPerson,
  invalidateFillApplication,
  pickOwner,
} from '@/models/fillApplication'
import { OwnerChoiceList } from './OwnerChoiceList'

interface ChangeOwnerPickProps {
  orgId: string
  /** The org's business owners; the pick only shows with two or more. */
  owners: FillPerson[]
  currentOwnerId: string | undefined
}

export function ChangeOwnerPick({ orgId, owners, currentOwnerId }: ChangeOwnerPickProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(currentOwnerId ?? null)
  const [saving, setSaving] = useState(false)
  if (owners.length < 2) return null
  const current = owners.find((owner) => owner.id === currentOwnerId)

  async function save() {
    if (!selectedId) return
    setSaving(true)
    try {
      await pickOwner(orgId, selectedId)
      await invalidateFillApplication(queryClient, orgId)
      setOpen(false)
    } catch (error) {
      toast.error(fillApplicationErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span>The application is for {current?.name || current?.email || 'one of the business owners'}.</span>
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto px-0"
        onClick={() => {
          setSelectedId(currentOwnerId ?? null)
          setOpen(true)
        }}
      >
        Change owner
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Who is the application for?</DialogTitle>
            <DialogDescription>This customer has {owners.length} business owners.</DialogDescription>
          </DialogHeader>
          <OwnerChoiceList owners={owners} selectedId={selectedId} onSelect={setSelectedId} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={!selectedId || selectedId === currentOwnerId}
              loading={saving ? 'Saving…' : undefined}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
